import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useFlowgateResults, type FlowgateResult } from '@/api/flowgate';
import {
  useGetExperiment,
  type ExperimentDetail,
} from '@/api/experiment/experiment';
import { useGetExperimentGroup } from '@/api/project/project';
import CustomNoRowsOverlay from '@/components/CustomNoRowsOverlay';
import PageHeader from '@/components/PageHeader/PageHeader';
import { formatDate } from '@/utils/dateUtils';

// Map experiment.status -> MUI Chip color. Unknown values fall back to default.
const STATUS_CHIP_COLOR: Record<
  string,
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  pending: 'default',
  waiting: 'warning',
  in_progress: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

// Human-friendly labels for the experiment_type enum.
const EXPERIMENT_TYPE_LABEL: Record<string, string> = {
  single: 'Single',
  trio: 'Trio',
  custom: 'Custom',
};

// One labeled detail item in the header card.
const HeaderField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const ExperimentHeader = ({
  experiment,
  experimentId,
  resultCount,
  title,
}: {
  experiment: ExperimentDetail | null;
  experimentId: string;
  resultCount: number;
  title: string;
}) => {
  const status = experiment?.status;
  const statusColor = (status && STATUS_CHIP_COLOR[status]) || 'default';
  const typeLabel = experiment?.experiment_type
    ? EXPERIMENT_TYPE_LABEL[experiment.experiment_type] ??
      experiment.experiment_type
    : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 2,
        borderTop: '3px solid',
        borderTopColor: 'primary.main',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h2">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Experiment ID · {experimentId}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {status && (
            <Chip
              label={status.replace('_', ' ')}
              color={statusColor}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
          )}
          <Chip
            label={`${resultCount} result${resultCount === 1 ? '' : 's'}`}
            variant="outlined"
            size="small"
          />
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr 1fr',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <HeaderField label="Type" value={typeLabel} />
        <HeaderField
          label="Pipeline"
          value={experiment?.flowgate_pipeline_id ?? null}
        />
        <HeaderField
          label="Samples"
          value={experiment?.samples?.length ?? null}
        />
        <HeaderField
          label="Created"
          value={
            experiment?.created_at
              ? formatDate(new Date(experiment.created_at))
              : null
          }
        />
      </Box>

      {experiment?.description && (
        <Box sx={{ mt: 2 }}>
          <HeaderField label="Description" value={experiment.description} />
        </Box>
      )}
    </Paper>
  );
};

const NoFlowgateResultsOverlay = () => (
  <CustomNoRowsOverlay message="No Flowgate results yet for this experiment." />
);

const MetadataPanel = ({ row }: { row: FlowgateResult }) => {
  if (row.columns == null && row.metadata == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        No additional metadata.
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {row.columns != null && (
        <Box>
          <Typography variant="subtitle2">columns</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              p: 1.5,
              borderRadius: 1.5,
              overflow: 'auto',
              maxHeight: 240,
              fontSize: '0.8rem',
              m: 0,
            }}
          >
            {JSON.stringify(row.columns, null, 2)}
          </Box>
        </Box>
      )}
      {row.metadata != null && (
        <Box>
          <Typography variant="subtitle2">metadata</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              p: 1.5,
              borderRadius: 1.5,
              overflow: 'auto',
              maxHeight: 240,
              fontSize: '0.8rem',
              m: 0,
            }}
          >
            {JSON.stringify(row.metadata, null, 2)}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

const FlowgateResultsPage = () => {
  const { experimentId } = useParams<{ experimentId: string }>();
  const navigate = useNavigate();
  const { data } = useFlowgateResults(experimentId);
  const { data: experiment } = useGetExperiment(experimentId);
  // Experiment.name doesn't exist on the BE model — the user-typed name is
  // stored on the parent ExperimentGroup. Fetch it so the header can show
  // something meaningful instead of falling back to "Experiment".
  const { data: experimentGroup } = useGetExperimentGroup(
    experiment?.experiment_group ?? undefined,
  );
  const headerTitle = experimentGroup?.name || 'Experiment';
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const columns: GridColDef<FlowgateResult>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'file_type',
      headerName: 'Type',
      width: 120,
    },
    {
      field: 'row_count',
      headerName: 'Rows',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value: number | null) =>
        value == null ? '—' : value.toLocaleString(),
    },
    {
      field: 'file_url',
      headerName: 'File',
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueGetter: (value: string) =>
        value ? formatDate(new Date(value)) : '',
    },
    {
      field: 'actions',
      headerName: '',
      width: 260,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isOpen = expandedId === params.row.id;
        return (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                navigate(`/result/experiment/${experimentId}/${params.row.id}`)
              }
            >
              Open
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpandedId(isOpen ? null : params.row.id)}
            >
              {isOpen ? 'Hide metadata' : 'Show metadata'}
            </Button>
          </Stack>
        );
      },
    },
  ];

  const expandedRow = data.find((r) => r.id === expandedId);

  return (
    <Box
      sx={{ width: '100%', maxWidth: 1680, mx: 'auto', p: { xs: 2, md: 3 } }}
    >
      <PageHeader
        eyebrow="Result workspace"
        title="Experiment results"
        description="Review generated result files and open a dataset for variant interpretation."
        actions={
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            variant="outlined"
            size="small"
          >
            Back
          </Button>
        }
      />

      <ExperimentHeader
        experiment={experiment}
        experimentId={experimentId ?? ''}
        resultCount={data.length}
        title={headerTitle}
      />

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2.25, pb: 1 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Typography variant="h3">Available result files</Typography>
              <Typography variant="body2" color="text.secondary">
                Select a result file to begin variant prioritization and
                validation.
              </Typography>
            </Box>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${data.length} file${data.length === 1 ? '' : 's'}`}
            />
          </Stack>
        </Box>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={data}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            slots={{ noRowsOverlay: NoFlowgateResultsOverlay }}
            sx={{
              '--DataGrid-overlayHeight': '120px',
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'background.default',
                borderBottomColor: 'divider',
              },
              '& .MuiDataGrid-cell': {
                borderBottomColor: 'divider',
              },
            }}
          />
        </Box>
        <Collapse in={expandedRow != null} unmountOnExit>
          {expandedRow && (
            <Box
              sx={{
                mx: { xs: 1.5, md: 2 },
                mb: 2,
                p: { xs: 1.5, md: 2 },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {expandedRow.name} — metadata
              </Typography>
              <MetadataPanel row={expandedRow} />
            </Box>
          )}
        </Collapse>
      </Paper>
    </Box>
  );
};

export default FlowgateResultsPage;
